import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { authCodeFlowConfig } from './auth-config';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { IRemoteXPACPortalState } from './utils/interfaces/store.interfaces';
import { ActionsSubject, Store } from '@ngrx/store';
import { AuthActionsTypes, loginOpenIdAction } from './store/actions/auth.actions';
import { StoreErrorService } from './services/store-error.services';
import { HeaderService } from './angular-core/services/header.service';
import { ActivatedRoute, Route, Router, RouterOutlet } from '@angular/router';
import { LocalizationService } from './angular-core/services/localization.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { slider } from './modules/animations/route-animations';
import { AppInsightsService } from './angular-core/services/appinsights.service';
import { NotificationService } from './angular-core/services/notification.service';
import { selectAuth, selectUserData } from './store/selectors/auth.selectors';
import { IUserData } from './utils/interfaces/auth.interfaces';
import { filter } from 'rxjs';
import { NotificationType } from './utils/enums/notifications.enum';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { PreferencesService } from './angular-core/services/preferences.service';
import { environment } from 'src/environments/environment';
import { FooterItemsService } from './services/footer-items.service';
import { FooterItem } from './angular-core/components/navigation/footer-base/footer-base.component';

const VALID_THEMES: string[] = [
  'default-theme',
  'light-theme'
];

@UntilDestroy()
@Component({
  selector: 'xpac-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    slider
  ]
})
export class AppComponent implements OnInit, AfterViewChecked {
  public showfooter: boolean = true;
  private theme: string = 'default-theme';
  private hasLogin: boolean = false;
  private hasLocalization: boolean = false;
  private footerItems: FooterItem[] = [];

  constructor(
    private appInsightsService: AppInsightsService,
    private oAuthService: OAuthService,
    private storeErrorService: StoreErrorService,
    private store: Store<IRemoteXPACPortalState>,
    private router: Router,
    private route: ActivatedRoute,
    private actionsListener$: Actions,
    private cdr: ChangeDetectorRef,
    private localization: LocalizationService,
    private actionsSubject$: ActionsSubject,
    private headerService: HeaderService,
    private notificationService: NotificationService,
    private footerItemsService: FooterItemsService,
    private preferences: PreferencesService,
  ) {
    // Uncomment this to log all actions, useful for debugging
    // this.actionsListener$.subscribe(a => console.log(a));

    this.appInsightsService.setRoleName("portal");
    this.appInsightsService.logTrace("Starting portal");
    // The header service MUST be instantiated by the app component or we will miss events
    // To prevent the typescript compiler from trying anything funny we make sure to call a function on it
    this.headerService.refreshHeaderTitle(route.snapshot);
    this.oAuthService.configure(authCodeFlowConfig);
    this.oAuthService.setupAutomaticSilentRefresh();
    this.oAuthService.tokenValidationHandler = new JwksValidationHandler();
  }

  public get isLoading(): boolean {
    return !this.hasLogin || !this.hasLocalization;
  }

  async ngOnInit(): Promise<void> {
    // Instantly load the theme from local storage to prevent the page flickering in the wrong theme color while we load other data
    const theme: string = this.preferences.getThemeFromLocalStorage();
    if (theme) {
      this.updateTheme(theme);
    }

    // Configure the preferences service
    this.preferences.configureUrls(environment.identityMgmtApiUrl);

    // Set up reload when access token changes so the correct user's stuff is loaded
    this.store.select(selectAuth)
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe(async (auth) => {
        if (auth.accessToken) {
          this.preferences.configureToken(auth.accessToken);
          await this.preferences.load();
        }
      });

    const url = new URL(window.location.href);
    const idToken = url.searchParams.get("id_token");
    if (idToken) {
      url.searchParams.delete("id_token");
      window.history.replaceState({}, "", url.href);
      this.oAuthService.logOut();
    }
    this.store.dispatch(loginOpenIdAction({ idTokenHint: idToken }));

    // Localization data should be loaded as soon as possible
    const localizationLoader = this.localization.load(['nl', 'de', 'fr'], 'assets/date-formats.json').then(() => {
      // Language defaults cannot be set until the languages are loaded
      this.preferences.setDefaults(
        this.localization.autoDetectLanguage(),
        this.localization.autoDetectLocale(),
        'default-theme'
      );
    });

    // Only start listing to errors when the login was successful
    this.actionsSubject$
      .pipe(ofType(AuthActionsTypes.LOGIN_LOGIN_OPENID_SUCCESS))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.storeErrorService.startErrorListener();
      });

    this.actionsSubject$
      .pipe(ofType(AuthActionsTypes.LOGIN_AUTH_INFO_SUCCESS))
      .pipe(untilDestroyed(this))
      .subscribe(async () => {
        this.hasLogin = true;
        // fetch the footeritems once
        this.footerItems = await this.footerItemsService.getFooterItemsFooter();

        this.cdr.markForCheck();
      });

    this.actionsSubject$
      .pipe(ofType(AuthActionsTypes.INVALID_TOKENS, AuthActionsTypes.TOKEN_REFRESH_FAILED))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        // Try to log in again
        this.store.dispatch(loginOpenIdAction({}));
      });

    // Notifier for impersonation
    this.store.select(selectUserData)
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe((user: IUserData) => {
        if (user.impersonator) {
          this.notificationService.raiseNotification({
            type: NotificationType.ImpersonationActive,
            params: {
              email: user.email,
              impEmail: user.impersonator.email,
            }
          });
        }
        else {
          this.notificationService.clearNotification(NotificationType.ImpersonationActive);
        }
      });

    // Register listener to update theme when possible
    this.preferences.theme
      .pipe(
        untilDestroyed(this),
        filter(Boolean),
      )
      .subscribe((theme) => {
        this.updateTheme(theme);
      });

    // Load initial user preferences now that localization is ready
    await localizationLoader;
    this.hasLocalization = true;
    await this.preferences.load();
    this.cdr.markForCheck();
  }

  public ngAfterViewChecked(): void {
    this.notificationService.fixElementSizing();
  }

  public prepareRoute(outlet: RouterOutlet): boolean {
    const animation: string =  outlet.activatedRouteData['animation'];
    if(this.footerItems.findIndex(fi => fi.route === animation) !== -1 && animation !== "more" && animation !== "insights") {
      // Direct footer item, always ignore the animation
      return false;
    }

    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
  }

  private updateTheme(theme: string): void {
    if (!VALID_THEMES.includes(theme)) {
      console.error(`Theme '${theme}' is not supported!`);
      return;
    }

    const body = document.getElementsByTagName('body')[0];
    const html = document.documentElement;
    body.classList.remove(this.theme);
    body.classList.add(theme);
    html.classList.remove(this.theme);
    html.classList.add(theme);

    this.theme = theme;
  }
}
